import React, { useState, useEffect } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Compass, Map, BookOpen, User, LogOut, Menu, X, Route, ArrowLeft, Plus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useStore } from '../store/useStore';
import { useI18n } from '../i18n';
import { useTheme } from '../themes';
import Search from '../components/Search';
import BottomNav from '../components/BottomNav';

const Layout: React.FC = () => {
  const { user, darkMode, reset } = useStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const { isSkeuomorphism, isNeubrutalism } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    // Clear demo auth
    localStorage.removeItem('demo_auth');
    reset();
    navigate('/');
  };

  const isActive = (path: string) => location.pathname === path;
  const canGoBack = location.pathname !== '/' && window.history.length > 1;

  const navLinks = [
    { path: '/', label: t.nav.feed, icon: Compass },
    { path: '/map', label: t.nav.map, icon: Map },
    ...(user ? [
      { path: '/my-shifts', label: t.nav.myShifts, icon: BookOpen },
      { path: '/trails', label: t.nav.trails, icon: Route },
    ] : []),
  ];

  return (
    <div className={`min-h-screen flex flex-col transition-colors duration-500 ${darkMode ? 'bg-neutral-950 text-neutral-100' : 'bg-neutral-50 text-neutral-900'}`}>
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'py-2' : 'py-4'
        }`}>
        <div className="max-w-6xl mx-auto px-4">
          <div className={`glass rounded-2xl px-4 sm:px-6 h-14 flex items-center justify-between transition-all duration-300 ${scrolled ? 'shadow-premium' : 'shadow-soft'
            }`}>
            <div className="flex items-center gap-3">
              {/* Back button for mobile */}
              {canGoBack && (
                <button
                  onClick={() => navigate(-1)}
                  className="md:hidden p-2 -ml-2 rounded-xl text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                  title={t.common.back}
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
              )}

              <Link to="/" className="flex items-center gap-2 group">
                <div className="w-9 h-9 bg-brand-600 rounded-xl flex items-center justify-center shadow-lg shadow-brand-500/20 group-hover:scale-105 transition-transform duration-300">
                  <Compass className="w-5 h-5 text-white" />
                </div>
                <span className="text-lg font-bold tracking-tight hidden sm:block">
                  Proof of <span className="text-brand-600">Learning</span>
                </span>
              </Link>
            </div>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map(link => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 flex items-center gap-2 ${isActive(link.path)
                      ? 'bg-brand-50 dark:bg-brand-900/20 text-brand-600 dark:text-brand-400'
                      : 'text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                    }`}
                >
                  {link.icon && <link.icon className="w-4 h-4" />}
                  {link.label}
                </Link>
              ))}
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              {/* Search - visible on all screens */}
              <Search />

              {user ? (
                <div className="hidden md:flex items-center gap-3">
                  <Link
                    to="/profile"
                    className={`p-2.5 rounded-xl transition-all ${isActive('/profile')
                        ? 'bg-brand-50 dark:bg-brand-900/20 text-brand-600 dark:text-brand-400'
                        : 'text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                      }`}
                  >
                    <User className="w-5 h-5" />
                  </Link>
                </div>
              ) : (
                <Link
                  to="/login"
                  className="hidden md:inline-flex px-4 py-2 bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 rounded-xl font-medium hover:opacity-90 transition-all active:scale-95"
                >
                  {t.nav.signIn}
                </Link>
              )}

              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2.5 rounded-xl text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Nav Dropdown */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="md:hidden absolute top-full left-0 right-0 mt-2 px-4"
            >
              <div className="glass rounded-2xl p-3 space-y-1 shadow-premium">
                {navLinks.map(link => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${isActive(link.path)
                        ? 'bg-brand-50 dark:bg-brand-900/20 text-brand-600 dark:text-brand-400'
                        : 'text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                      }`}
                  >
                    {link.icon && <link.icon className="w-5 h-5" />}
                    {link.label}
                  </Link>
                ))}
                {user && (
                  <>
                    <Link
                      to="/profile"
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${isActive('/profile')
                          ? 'bg-brand-50 dark:bg-brand-900/20 text-brand-600 dark:text-brand-400'
                          : 'text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                        }`}
                    >
                      <User className="w-5 h-5" />
                      {t.nav.profile}
                    </Link>
                    <button
                      onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium w-full text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    >
                      <LogOut className="w-5 h-5" />
                      {t.nav.signOut}
                    </button>
                  </>
                )}
                {!user && (
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 rounded-xl font-medium transition-colors"
                  >
                    {t.nav.signIn}
                  </Link>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      <main className="max-w-6xl mx-auto px-4 pt-24 pb-24 md:pb-16 flex-1 w-full">
        <Outlet />
      </main>

      {/* Bottom Navigation for Mobile */}
      <BottomNav />

      {/* Floating Add Shift Button - Theme Aware */}
      {user && (
        <Link
          to="/create"
          className={`fixed bottom-24 right-4 md:bottom-8 md:right-8 z-50 w-14 h-14 flex items-center justify-center transition-all active:scale-95 fab-btn ${isNeubrutalism
              ? 'bg-yellow-400 text-black'
              : isSkeuomorphism
                ? 'rounded-xl'
                : 'bg-brand-600 text-white rounded-full shadow-xl shadow-brand-500/30 hover:bg-brand-700 hover:scale-110'
            }`}
          title={t.nav.addShift}
        >
          <Plus className="w-6 h-6" />
        </Link>
      )}

      <footer className="hidden md:block py-10 border-t border-neutral-200 dark:border-neutral-800">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-brand-600 rounded-lg flex items-center justify-center">
                <Compass className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold tracking-tight text-sm">Proof of Learning</span>
            </div>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              Documenting the evolution of human understanding.
            </p>
            <div className="flex items-center gap-6">
              <a href="#" className="text-xs text-neutral-500 hover:text-brand-600 transition-colors">Privacy</a>
              <a href="#" className="text-xs text-neutral-500 hover:text-brand-600 transition-colors">Terms</a>
              <a href="#" className="text-xs text-neutral-500 hover:text-brand-600 transition-colors">Contact</a>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-neutral-100 dark:border-neutral-900 text-center">
            <p className="text-xs text-neutral-400">© 2025 Proof of Learning. Built for the curious.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
